// Core
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

// Components
import { Button, Chip, FieldSelect, FieldText } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { openNotificationPopup, openSuccessAlert } from "state/application/applicationSlice";
import {
  useCreateServiceAuthMutation,
  useDeleteServiceAuthMutation,
  useTestServiceAuthMutation,
  useUpdateServiceAuthMutation,
} from "state/integrations/integrationsApiSlice";

const AUTH_TYPES = [
  { value: "JWT_TOKEN", label: "JWT" },
  { value: "OAUTH2", label: "OAuth2" },
];

/* Sensible defaults for authenticating GDBN against this RECreation instance:
   its login endpoint takes username/password and answers with access_token. */
const blankAuth = () => ({
  type: "JWT_TOKEN",
  loginUrl: `${window.location.origin}/core/api/auth/login`,
  usernameField: "username",
  username: "",
  passwordField: "password",
  password: "",
  tokenField: "access_token",
  tokenUri: "",
  clientId: "",
  clientSecret: "",
});

function IntegrationServiceAuth({ integrationId, serviceId, service }) {
  const { t } = useTranslation(["settings"]);
  const dispatch = useDispatch();

  // The services listing already embeds the configured auth method (v1 calls
  // it authMethod, v2 serviceAuth); GDBN's dedicated per-service GET is
  // unreliable (returns 500s), so the embedded object is the source of truth.
  const existing = service?.authMethod || service?.serviceAuth || null;
  const configured = !!existing;

  const [createAuth, { isLoading: isCreating }] = useCreateServiceAuthMutation();
  const [updateAuth, { isLoading: isUpdating }] = useUpdateServiceAuthMutation();
  const [deleteAuth, { isLoading: isDeleting }] = useDeleteServiceAuthMutation();
  const [testAuth, { isLoading: isTesting }] = useTestServiceAuthMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankAuth());

  useEffect(() => {
    const merged = blankAuth();
    if (existing) {
      Object.keys(merged).forEach((k) => {
        if (existing[k]) merged[k] = existing[k];
      });
      // secrets are write-only in the form; never prefill them
      merged.password = "";
      merged.clientSecret = "";
    }
    setForm(merged);
  }, [existing, serviceId]);

  const setField = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  const notifyError = (err) => {
    dispatch(
      openNotificationPopup({
        notification: err?.data?.detail || t("integrations.error_generic"),
        type: "error",
      })
    );
  };

  const buildPayload = () => {
    if (form.type === "OAUTH2") {
      return {
        type: "OAUTH2",
        tokenUri: form.tokenUri,
        clientId: form.clientId,
        clientSecret: form.clientSecret || undefined,
      };
    }
    return {
      type: "JWT_TOKEN",
      loginUrl: form.loginUrl,
      usernameField: form.usernameField || undefined,
      username: form.username,
      passwordField: form.passwordField || undefined,
      password: form.password || undefined,
      tokenField: form.tokenField || undefined,
    };
  };

  const onSave = async () => {
    try {
      if (configured && existing?.id) {
        await updateAuth({
          integrationId,
          serviceId,
          authMethodId: existing.id,
          body: buildPayload(),
        }).unwrap();
      } else {
        await createAuth({ integrationId, serviceId, body: buildPayload() }).unwrap();
      }
      dispatch(openSuccessAlert({ title: t("integrations.auth.saved_success") }));
    } catch (err) {
      notifyError(err);
    }
  };

  const onDelete = async () => {
    try {
      await deleteAuth({ integrationId, serviceId }).unwrap();
      dispatch(openSuccessAlert({ title: t("integrations.auth.deleted_success") }));
    } catch (err) {
      notifyError(err);
    }
  };

  const onTest = async () => {
    try {
      const result = await testAuth({ integrationId, body: buildPayload() }).unwrap();
      dispatch(
        openSuccessAlert({
          title: t("integrations.auth.test_result", { status: result?.statusCode }),
        })
      );
    } catch (err) {
      notifyError(err);
    }
  };

  const busy = isCreating || isUpdating || isDeleting || isTesting;

  return (
    <div
      className="mt-[14px]"
      style={{ padding: 14, background: "var(--surface-2)", borderRadius: "var(--radius-sm)" }}
    >
      <div className="flex items-center gap-[10px] flex-wrap">
        <div className="flex-1 flex items-center gap-[10px] min-w-0">
          <span className="text-[13.5px] font-semibold text-ink">
            {t("integrations.auth.title")}
          </span>
          <Chip tone={configured ? "teal" : "amber"}>
            {configured
              ? t("integrations.auth.configured", { type: existing?.type })
              : t("integrations.auth.not_configured")}
          </Chip>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)}>
          <Icon name={open ? "expand_less" : "expand_more"} />
          {open ? t("integrations.auth.hide") : t("integrations.auth.manage")}
        </Button>
      </div>
      {!open && (
        <div className="text-[11.5px] text-ink-3 mt-[2px]">{t("integrations.auth.hint")}</div>
      )}

      {open && (
        <div className="mt-3">
          {/* items-start: labels are single-line here, so rows align by the top
              and the password hint hangs below without pushing neighbours */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[12px] items-start">
            <FieldSelect
              label={t("integrations.auth.type")}
              value={form.type}
              options={AUTH_TYPES}
              onChange={setField("type")}
            />
            {form.type === "JWT_TOKEN" ? (
              <>
                <FieldText
                  label={t("integrations.auth.login_url")}
                  value={form.loginUrl}
                  onChange={setField("loginUrl")}
                />
                <FieldText
                  label={t("integrations.auth.username")}
                  value={form.username}
                  onChange={setField("username")}
                />
                <FieldText
                  label={t("integrations.auth.password")}
                  type="password"
                  reveal
                  value={form.password}
                  hint={configured ? t("integrations.auth.password_edit_hint") : null}
                  onChange={setField("password")}
                />
                <FieldText
                  label={t("integrations.auth.username_field")}
                  value={form.usernameField}
                  onChange={setField("usernameField")}
                />
                <FieldText
                  label={t("integrations.auth.password_field")}
                  value={form.passwordField}
                  onChange={setField("passwordField")}
                />
                <FieldText
                  label={t("integrations.auth.token_field")}
                  value={form.tokenField}
                  onChange={setField("tokenField")}
                />
              </>
            ) : (
              <>
                <FieldText
                  label={t("integrations.auth.token_uri")}
                  value={form.tokenUri}
                  onChange={setField("tokenUri")}
                />
                <FieldText
                  label={t("integrations.auth.client_id")}
                  value={form.clientId}
                  onChange={setField("clientId")}
                />
                <FieldText
                  label={t("integrations.auth.client_secret")}
                  type="password"
                  reveal
                  value={form.clientSecret}
                  hint={configured ? t("integrations.auth.password_edit_hint") : null}
                  onChange={setField("clientSecret")}
                />
              </>
            )}
          </div>
          <div className="flex items-center justify-end gap-[10px] mt-3 flex-wrap">
            {configured && (
              <Button
                variant="ghost"
                size="sm"
                className="!text-danger"
                disabled={busy}
                onClick={onDelete}
              >
                <Icon name="delete" />
                {t("integrations.auth.delete")}
              </Button>
            )}
            <Button variant="ghost" size="sm" disabled={busy} onClick={onTest}>
              <Icon name="send" />
              {t("integrations.auth.test")}
            </Button>
            <Button size="sm" disabled={busy} onClick={onSave}>
              <Icon name="save" />
              {t("integrations.auth.save")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

IntegrationServiceAuth.propTypes = {
  integrationId: PropTypes.string.isRequired,
  serviceId: PropTypes.string.isRequired,
  service: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    authMethod: PropTypes.object, // GDBN v1 embeds the auth method here
    // eslint-disable-next-line react/forbid-prop-types
    serviceAuth: PropTypes.object, // GDBN v2 name for the same object
  }),
};

IntegrationServiceAuth.defaultProps = { service: null };

export default IntegrationServiceAuth;
